import { defineStore } from 'pinia';

import { sportsList, BannerEventBoxDefault } from '@skeleton/core/consts/sportsConfig';
import { formatKebabCase } from '@skeleton/utils/formatKebabCase';

const MOBILE = 1;
const DESKTOP = 2;

interface IStartParams {
  accountId?: string;
  lobbyUrl: string;
  locale: string;
  countryCode?: string;
  demoMode: boolean;
  platform: number;
  provider: string;
}

interface IAltenarStoreState {
  startParams: Maybe<IStartParams>;
  altenarToken: string;
  widgetSportBookInstance: any;
  widgetWTopEventsCarouselInstance: any;
  widgetWLiveSportCarouselInstance: any;
  isAltenarInitiated: boolean;
  sportInfoCache: {[sportId: string]: ISportInfo};
}

export interface IWidgetParams {
  page?: Maybe<string>;
  championshipIds?: number[];
  categoryIds?: number[];
  sportId?: number;
  eventId?: number;
}

export interface IWidgetDetails {
  page?: string;
  id?: number;
  widget?: string;
  championshipIds?: number[];
  champId?: number;
  event?: {
    id: number;
    name?: string;
    liveTime?: string;
    startDate?: string;
  };
  periodType?: number;
  sportTypeId?: number;
  championships?: Array<{
    name: string;
    id: number;
  }>;
  categories?: Array<{
    name: string;
    id: number;
  }>;
  sport?: {
    name: string;
    id: number;
    typeId?: number;
  };
}

export interface ICategory {
  CategoryId: number;
  Name: string;
  Championships?: IChamp[];
}

export interface IChamp {
  ChampionshipId: number;
  Name: string;
}

export interface ISportInfo {
  SportId: number;
  Name: string;
  Categories: ICategory[];
}

export const useAltenarStore = defineStore('altenarStore', {
  state: (): IAltenarStoreState => ({
    startParams: null,
    altenarToken: '',
    widgetSportBookInstance: null,
    widgetWTopEventsCarouselInstance: null,
    widgetWLiveSportCarouselInstance: null,
    isAltenarInitiated: false,
    sportInfoCache: {},
  }),

  getters: {
    sportsIdsBgs(): Maybe<{ [key: number]: string }> {
      const {
        public: { gamehubCdn },
      } = useRuntimeConfig();

      return sportsList.reduce(function(acc: { [key: number]: string }, sport) {
        if (sport.widgetBgUrl) {
          acc[sport.id] = gamehubCdn + sport.widgetBgUrl;
        }
        return acc;
      }, {});
    },
    isCurrentLocaleIsEn() {
      const { currentLocale } = useGlobalStore();
      return currentLocale?.code.toLowerCase() === 'en';
    },
  },

  actions: {
    setStartParams() {
      const { activeAccount } = useWalletStore();
      const { currentLocale, headerCountry } = useGlobalStore();
      const { profile } = useProfileStore();

      const deviceStore = useDeviceStore();
      const { isMobile } = storeToRefs(deviceStore);

      this.startParams = {
        accountId: activeAccount?.id,
        lobbyUrl: window.location.origin,
        locale: currentLocale?.code || 'en',
        countryCode: profile?.country || headerCountry || 'UA',
        demoMode: false,
        platform: isMobile.value ? MOBILE : DESKTOP,
        provider: 'altenar',
      };
    },

    sportNameById(sportId: number): Maybe<string> {
      return sportsList.find(item => item.id === sportId)?.code;
    },

    async getChampInfo(sportID: number, categoryId: number, champParam: number | string, returnName = true): Promise<Maybe<string | number>> {
      try {

        const { getSportInfo } = useCoreGamesApi();
        const data:ISportInfo = this.sportInfoCache[`sport_${sportID}`] ?? await getSportInfo(sportID);

        if (!data) return null;
        this.sportInfoCache[`sport_${sportID}`] = data;

        const category = data.Categories?.find(
          (cat: ICategory) => cat.CategoryId === categoryId,
        );

        if (returnName) {
          const champ = category?.Championships?.find(
            (champ: IChamp) => champ.ChampionshipId === champParam,
          );

          return champ?.Name ?? null;
        }

        const champ = category?.Championships?.find(
          (champ: IChamp) => this.formatName(champ.Name) === this.formatName(champParam),
        );

        return champ?.ChampionshipId;
      } catch (e) {
        console.error('Something went wrong with sport champ info fetching!', e);
        return null;
      }
    },

    async getCategoryInfo(sportID: number, categoryParam: number | string, returnName = true): Promise<Maybe<string | number>> {
      try {
        const { getSportInfo } = useCoreGamesApi();
        const data:ISportInfo = this.sportInfoCache[`sport_${sportID}`] ?? await getSportInfo(sportID);

        if (!data) return null;
        this.sportInfoCache[`sport_${sportID}`] = data;

        if (returnName) {
          const category: Maybe<ICategory> = data.Categories?.find((category: ICategory) => category.CategoryId === categoryParam);
          return category?.Name;
        }

        const category: Maybe<ICategory> = data.Categories?.find(
          (category: ICategory) => this.formatName(category.Name) === this.formatName(categoryParam)
        );

        return category?.CategoryId;
      } catch (e) {
        console.error('Something went wrong with sport category info fetching!', e);
        return null;
      }
    },

    sportIdByName(name: string): number | undefined {
      const sport = sportsList.find(sport => sport.code === name);
      return sport?.id;
    },

    async buildWidgetUrl(details: IWidgetDetails): Promise<string> {
      const parts: string[] = [];

      const GETParams = new URLSearchParams({
        page: details.page ? details.page : 'overview',
      });

      let sportName = '';
      if (details.sport) {
        sportName = this.isCurrentLocaleIsEn ? details.sport?.name : this.sportNameById(details.sport?.id);

        if (sportName) {
          parts.push(formatKebabCase(sportName));
        }
      }

      const categoryName = await this.getCategoryName(details);

      if (categoryName) {
        parts.push(formatKebabCase(categoryName));
      }

      const champName = await this.getChampName(details);

      if (champName) {
        parts.push(formatKebabCase(champName));
      }

      if (details.event?.id && details.event?.name) {
        parts.push(`${details.event.id}-${formatKebabCase(details.event.name)}`);
      }

      if (details.event?.id && !details.sport?.id) {
        GETParams.set('eventId', details.event?.id);
      }

      if (details.categories?.length && (details.categories?.length > 1 || !this.isCurrentLocaleIsEn)) {
        GETParams.set('categoryIds', details.categories.map(({ id }) => id).join(','));
      }

      if (details.championships?.length && (details.championships?.length > 1 || !this.isCurrentLocaleIsEn)) {
        const championshipIds = details?.champId ||
          details.championshipIds?.join(',') ||
          details.championships.map(({ id }) => id).join(',');
        GETParams.set('championshipIds', championshipIds);
      }

      return `/sport/${parts.join('/')}${GETParams.toString() ? `?${GETParams.toString()}` : ''}`;
    },

    async getChampName(details: IWidgetDetails): Promise<string> {
      let champName = '';

      if (this.isCurrentLocaleIsEn || !details.sport?.id) {
        champName = details.championships?.[0].name;
      } else {
        const fetchedChampName = await this.getChampInfo(
          details.sport?.id,
          details.categories?.[0].id,
          details?.champId || details.championshipIds?.[0] || details.championships?.[0].id,
        );

        champName = fetchedChampName ? fetchedChampName : details.championships?.[0].name;
      }

      return champName ?? '';
    },

    async getCategoryName(details: IWidgetDetails): Promise<string> {
      let categoryName = '';

      if (this.isCurrentLocaleIsEn || !details.sport?.id) {
        categoryName = details.categories?.[0]?.name;
      } else {
        const fetchedCategoryName = await this.getCategoryInfo(
          details.sport?.id,
          details.categories?.[0]?.id,
        );

        categoryName = fetchedCategoryName ? fetchedCategoryName : details.categories[0].name;
      }

      return categoryName ?? '';
    },

    async getAltenarToken(): Promise<void> {
      const { profile } = useProfileStore();
      if (!profile) {
        this.altenarToken = '';
        return;
      }

      try {
        const { getStartGame } = useCoreGamesApi();
        const startResponse = await getStartGame('altenar-sportsbook-altenar-sportsbook', this.startParams);
        if (startResponse.token) {
          this.altenarToken = startResponse.token;
        }
      } catch (e) {
        console.log('Error Altenar Token:', e);
      }
    },

    async initAltenarScript(): Promise<void> {
      this.setStartParams();
      await this.getAltenarToken();

      if (window.altenarWSDK) {
        this.initAltenarWSDK();
      } else {
        const { addAltenarScript } = useProjectMethods();
        const altenarScript = addAltenarScript();
        altenarScript.onload = () => {
          this.initAltenarWSDK();
        };
      }
    },

    initAltenarWSDK(): void {
      const runtimeConfig = useRuntimeConfig();
      const { currentLocale } = useGlobalStore();
      const { getColorMode } = useColorModeLogic();

      window.altenarWSDK.init({
        integration: runtimeConfig.public.altenarIntegration,
        dateFormat: {
          time: 'HH:mm',
          day: 'DD',
          date: 'DD.MM.YY',
          dateTime: 'DD.MM.YY, HH:mm',
          dayMonth: 'DD.MM',
          dayMonthTime: 'DD.MM HH:mm',
        },
        culture: currentLocale?.localeCode
          ? currentLocale?.localeCode.replace('es-MX', 'es-ES').replace('en-US', 'en-GB')
          : 'en-GB',
        token: this.altenarToken,
        themeName: getColorMode(),
      });
      this.isAltenarInitiated = true;

      useEvent('altenarWSDKInit');
    },

    waitForAltenarWSDKInit(): Promise<boolean> {
      return new Promise(resolve => {
        if (this.isAltenarInitiated) {
          resolve(true);
        }

        useListen('altenarWSDKInit', payload => {
          resolve(payload);
        });
      });
    },

    async updateAltenarToken(): Promise<void> {
      await this.getAltenarToken();
      const { getColorMode } = useColorModeLogic();

      window.altenarWSDK.set({
        token: this.altenarToken,
        themeName: getColorMode(),
      });
    },

    async addSportBookWidget(containerId: string, widgetParams?: Maybe<IWidgetParams>): Promise<boolean> {
      await this.waitForAltenarWSDKInit();

      return new Promise(resolve => {
        this.widgetSportBookInstance = window.altenarWSDK.addSportsBook({
          props: {
            ...widgetParams,
            routerType: 'memory',
            onRouterLoad: () => {
              resolve(true);
            },
            onRouteChangeDetailed: async (details: IWidgetDetails) => {
              window.history.pushState({}, '', await this.buildWidgetUrl(details));
            },
          },
          container: document.getElementById(containerId),
        });
      });
    },

    async removeSportBookWidget(): Promise<void> {
      if (this.widgetSportBookInstance !== null) {
        await this.widgetSportBookInstance.remove();
      }

      this.widgetSportBookInstance = null;
    },

    async updateSportBookWidget(widgetParams?: Maybe<IWidgetParams>): Promise<void> {
      if (widgetParams) {
        window.altenarWSDK.set(widgetParams);
      }
    },

    async removeTopEventsCarouselWidget(): Promise<void> {
      await this.widgetWTopEventsCarouselInstance?.remove();
    },

    async addTopEventsCarouselWidget(containerId: string, sportId?: number): Promise<void> {
      await this.waitForAltenarWSDKInit();

      this.widgetWTopEventsCarouselInstance = window.altenarWSDK.addWidget({
        widget: 'WTopEventsCarousel',
        tokens: {
          BannerEventBoxDefault,
        },
        props: {
          backgroundOverride: {
            sport: this.sportsIdsBgs,
          },
          imageType: 2,
          eventCount: 15,
          onEventSelect: async (event: IWidgetDetails) => {
            await navigateTo(await this.buildWidgetUrl(event));
          },
          onOddSelect: async odd => {
            const details = {
              event: {
                id: odd.eventId,
              },
              champId: odd.champId,
            };

            await navigateTo(await this.buildWidgetUrl(details));
          },
          sportId: sportId ?? undefined,
        },
        container: document.getElementById(containerId),
      });
    },

    async updateTopEventsCarouselWidget(sportId?: number): Promise<void> {
      this.widgetWTopEventsCarouselInstance?.update({ sportId });
    },

    async removeLiveSportCarouselWidget(): Promise<void> {
      await this.widgetWLiveSportCarouselInstance.remove();
    },

    async addLiveSportCarouselWidget(containerId: string, sportId?: number): Promise<void> {
      await this.waitForAltenarWSDKInit();

      this.widgetWLiveSportCarouselInstance = window.altenarWSDK.addWidget({
        widget: 'WLiveNow',
        tokens: {
          BannerEventBoxDefault,
        },
        props: {
          backgroundOverride: {
            sport: this.sportsIdsBgs,
          },
          eventCount: 7,
          onEventSelect: async (event: IWidgetDetails) => {
            await navigateTo(await this.buildWidgetUrl(event));
          },
          onOddSelect: async odd => {
            await navigateTo(`/sport/event/${odd.eventId}`);
          },
          sportId: sportId ?? undefined,
        },
        container: document.getElementById(containerId),
      });
    },

    async updateLiveSportCarouselWidget(containerId: string, sportId?: number): Promise<void> {
      await this.removeLiveSportCarouselWidget();
      await this.addLiveSportCarouselWidget(containerId, sportId);
    },

    formatName(str: string): string {
      return str
        .replace(/[(),.]/g, '')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
    },
  },
});
